/*
The Application belongs to Comprehend PT Inc.
    Author: Jacob Michalski
    Date Created: 09/17/2026
*/
// The onboarding checklist — same five items, same look, in the web app and
// the extension side panel (this component), and in the Playground (its own
// widget in cx_embed/apps/playground/public/tour.js). Progress lives on the
// user record (`onboarding` via /v1/user/settings, read back on
// /v1/user/stats), so it follows the account across all three surfaces.
// Design: docs/playground-onboarding-design.md §2.
import React, { useContext, useEffect, useState } from 'react';
import { GlobalContext } from '../../api/GlobalContext';
import { onboardingChecklistActive, advancedDone, advancedHasNew, basicDone } from './checklistActive';

const PLAYGROUND_HOSTS = ['playground.comprehendpt.ai', 'playground.dev.comprehendpt.com'];
function isPlaygroundOrigin() {
  const h = window.location.hostname;
  return PLAYGROUND_HOSTS.includes(h) || (h === 'localhost' && window.location.port === '3009');
}
function playgroundBase(apiClient) {
  const dev =
    /dev\.comprehendpt\.com$/.test(window.location.hostname) ||
    /api\.dev\./.test(apiClient?.apiClient?.defaults?.baseURL || '');
  return dev ? 'https://playground.dev.comprehendpt.com' : 'https://playground.comprehendpt.ai';
}

const CSS = `
  .cpt-ck { position: fixed; left: 16px; bottom: calc(16px + env(safe-area-inset-bottom, 0px)); z-index: 10000000;
    font-family: "Outfit", -apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif; font-size: 13.5px; line-height: 1.5; color: #1a1e1c; }
  .cpt-ck button { font: inherit; cursor: pointer; border: 0; background: none; color: inherit; }
  .cpt-ck .pill { display: flex; align-items: center; gap: 0; background: #1a1e1c; color: #fff; border-radius: 999px;
    padding: 6px; font-weight: 500; box-shadow: 0 24px 60px -24px rgba(26,30,28,.28); max-width: 48px; overflow: hidden;
    white-space: nowrap; transition: max-width .25s ease, gap .25s ease, padding .25s ease, background .15s; }
  .cpt-ck .pill > span:last-child { opacity: 0; transition: opacity .2s ease .05s; }
  .cpt-ck .pill:hover, .cpt-ck .pill:focus-visible { background: #2a312d; max-width: 420px; gap: 10px; padding: 6px 18px 6px 6px; }
  .cpt-ck .pill:hover > span:last-child, .cpt-ck .pill:focus-visible > span:last-child { opacity: 1; }
  /* First real note — the moment worth a celebration, right here in the panel. */
  .cpt-ck .cheer { position: fixed; inset: 0; z-index: 10000001; display: grid; place-items: center; pointer-events: none; }
  .cpt-ck .cheer canvas { position: absolute; inset: 0; width: 100%; height: 100%; }
  .cpt-ck .cheer .msg { position: relative; background: #fff; color: #1a1e1c; border-radius: 22px; padding: 22px 26px; text-align: center;
    box-shadow: 0 30px 80px -30px rgba(26,30,28,.45); animation: cpt-cheer-in .45s cubic-bezier(.2,.9,.3,1.2); max-width: min(360px, calc(100vw - 40px)); }
  .cpt-ck .cheer .msg .big { font-size: 40px; line-height: 1; margin-bottom: 8px; }
  .cpt-ck .cheer .msg h3 { margin: 0 0 4px; font-size: 20px; font-weight: 700; color: #1fae76; }
  .cpt-ck .cheer .msg p { margin: 0; font-size: 14px; color: #4b5552; }
  @keyframes cpt-cheer-in { from { transform: scale(.7); opacity: 0; } to { transform: scale(1); opacity: 1; } }
  .cpt-ck .ring { width: 36px; height: 36px; border-radius: 50%; display: grid; place-items: center; position: relative; flex: none; }
  .cpt-ck .ring::after { content: attr(data-n); position: absolute; inset: 4px; background: #1a1e1c; border-radius: 50%;
    display: grid; place-items: center; font-size: 11px; font-weight: 700; letter-spacing: .02em; }
  .cpt-ck .panel { width: min(360px, calc(100vw - 32px)); background: #fff; border: 1px solid #e3ebe7; border-radius: 22px;
    box-shadow: 0 24px 60px -24px rgba(26,30,28,.28); overflow: hidden; }
  .cpt-ck .hd { background: linear-gradient(160deg, #2dce8f 0%, #1fae76 100%); color: #fff; padding: 16px 18px; position: relative; overflow: hidden; }
  .cpt-ck .hd h3 { font-size: 16px; color: #fff; margin: 0; font-weight: 600; }
  .cpt-ck .hd small { opacity: .85; font-size: 12.5px; }
  .cpt-ck .hd .x { position: absolute; right: 14px; top: 10px; color: #fff; opacity: .8; font-size: 18px; }
  .cpt-ck .pbar { height: 6px; background: rgba(255,255,255,.28); border-radius: 3px; margin-top: 10px; overflow: hidden; }
  .cpt-ck .pbar i { display: block; height: 100%; background: #fff; border-radius: 3px; transition: width .4s; }
  .cpt-ck .ci { display: flex; gap: 12px; padding: 12px 18px; border-bottom: 1px solid #e3ebe7; align-items: flex-start; width: 100%; text-align: left; color: #1a1e1c; }
  .cpt-ck .ci:last-of-type { border-bottom: 0; }
  .cpt-ck .box { width: 22px; height: 22px; border-radius: 50%; border: 1.5px solid #c9d6d0; flex: none; display: grid; place-items: center; font-size: 12px; margin-top: 1px; }
  .cpt-ck .ci.done .box { background: #2dce8f; border-color: #2dce8f; color: #fff; }
  .cpt-ck .ci.done .n { text-decoration: line-through; color: #7d8683; }
  .cpt-ck .ci.next { background: #ebfff7; }
  .cpt-ck .ci.next .box { border-color: #2dce8f; border-width: 2px; }
  .cpt-ck .ci.lock { opacity: .5; cursor: default; }
  .cpt-ck .n { font-weight: 600; font-size: 14px; }
  .cpt-ck .d { font-size: 12.5px; color: #7d8683; }
  .cpt-ck .t { margin-left: auto; font-size: 12px; color: #7d8683; white-space: nowrap; padding-top: 3px; }
  .cpt-ck .go { display: inline-block; margin-top: 4px; font-size: 12.5px; font-weight: 600; color: #1fae76; }
  .cpt-ck .ft { padding: 10px 18px; font-size: 11.5px; color: #7d8683; background: #f6faf8; display: flex; justify-content: space-between; }
  .cpt-ck .ft button { color: #7d8683; text-decoration: underline; font-size: 11.5px; }
  .cpt-ck .how { padding: 16px 18px 14px; }
  .cpt-ck .how p { margin: 0 0 10px; font-size: 13.5px; }
  .cpt-ck .how ol { margin: 0 0 12px; padding-left: 0; list-style: none; display: flex; flex-direction: column; gap: 8px; }
  .cpt-ck .how li { display: flex; gap: 10px; align-items: flex-start; font-size: 13.5px; }
  .cpt-ck .how li .n { flex: none; width: 22px; height: 22px; border-radius: 50%; background: #1a1e1c; color: #fff; font-size: 12px; font-weight: 700; display: grid; place-items: center; }
  .cpt-ck .how .btns { display: flex; gap: 10px; align-items: center; }
  .cpt-ck .how .ok { background: #2dce8f; color: #fff; border-radius: 999px; padding: 9px 16px; font-weight: 700; font-size: 13px; }
  .cpt-ck .how .back { color: #7d8683; font-size: 13px; }
  @keyframes cpt-ck-pulse { 0%, 100% { outline-color: #2dce8f; } 50% { outline-color: rgba(45,206,143,.25); } }
`;

export function onboardingState(apiClient, isCX) {
  const o = (apiClient && apiClient.onboarding) || {};
  return {
    account: true,
    basic: basicDone(o),
    install: !!o.cx_authed || !!isCX,
    real: !!o.real,
    advanced: advancedDone(apiClient),
    dismissed: !!o.dismissed,
  };
}

const OnboardingChecklist = () => {
  const { apiClient } = useContext(GlobalContext);
  // Running inside the extension OR the self-hosted enterprise embed (partner
  // rollouts, e.g. PT Solutions) both mean "Comprehend is in the EMR".
  const isCX = window.location.protocol === 'chrome-extension:' || /(^|\.)embed\.comprehendpt\.com$/.test(window.location.hostname);
  const collapsedKey = () => `cpt-ck-collapsed:${(apiClient?.email || '').toLowerCase()}`;
  const wasCollapsed = () => { try { return localStorage.getItem(collapsedKey()) === '1'; } catch (e) { return false; } };
  // Open on first sight; once they collapse it, it stays a pill on later loads.
  const [open, setOpenState] = useState(() => !wasCollapsed());
  const setOpen = (v) => {
    setOpenState(v);
    if (!v) { try { localStorage.setItem(collapsedKey(), '1'); } catch (e) { /* ok */ } }
  };
  const [how, setHow] = useState(false); // "Use it on a real visit" explainer
  const [cheer, setCheer] = useState(false); // first real note landed
  const [, bump] = useState(0);

  useEffect(() => {
    const rerender = (e) => {
      bump((n) => n + 1);
      // The first real Comprehend is the whole point of onboarding — say so.
      if (e && e.detail && e.detail.step === 'real' && e.detail.value) {
        setOpen(false);
        setCheer(true);
        setTimeout(() => setCheer(false), 4200);
      }
    };
    window.addEventListener('comprehend:onboarding', rerender);
    // The api client is one long-lived object: React can't see `onboarding`,
    // `createdAt` or `features` land when /v1/user/stats answers. It tells
    // us through its profile callbacks — without this the list only showed
    // up after the next navigation.
    if (apiClient?.onProfileUpdated) apiClient.onProfileUpdated(rerender);
    return () => {
      window.removeEventListener('comprehend:onboarding', rerender);
      if (apiClient?.offProfileUpdated) apiClient.offProfileUpdated(rerender);
    };
  }, [apiClient]);
  // The account's own flag arrives with stats — re-evaluate "open" once it's there.
  useEffect(() => { if (apiClient?.email && wasCollapsed()) setOpenState(false); }, [apiClient?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  // Confetti for the cheer: a small canvas burst, no library.
  useEffect(() => {
    if (!cheer) return undefined;
    const canvas = document.getElementById('cpt-ck-confetti');
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvas.clientWidth * dpr; canvas.height = canvas.clientHeight * dpr; ctx.scale(dpr, dpr);
    const W = canvas.clientWidth, H = canvas.clientHeight;
    const colors = ['#2dce8f', '#1fae76', '#ffd166', '#ef476f', '#118ab2', '#fff'];
    const bits = Array.from({ length: 110 }, () => ({
      x: W / 2, y: H / 2, vx: (Math.random() - 0.5) * 14, vy: -Math.random() * 12 - 4,
      r: 4 + Math.random() * 5, c: colors[Math.floor(Math.random() * colors.length)], a: Math.random() * Math.PI, s: (Math.random() - 0.5) * 0.3,
    }));
    let raf; const t0 = performance.now();
    const tick = (t) => {
      ctx.clearRect(0, 0, W, H);
      const dt = (t - t0) / 1000;
      bits.forEach((b) => {
        b.vy += 0.35; b.x += b.vx; b.y += b.vy; b.vx *= 0.99; b.a += b.s;
        ctx.save(); ctx.translate(b.x, b.y); ctx.rotate(b.a); ctx.globalAlpha = Math.max(0, 1 - dt / 3.2);
        ctx.fillStyle = b.c; ctx.fillRect(-b.r / 2, -b.r / 2, b.r, b.r * 0.6); ctx.restore();
      });
      if (dt < 3.4) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [cheer]);

  // Point at the New Visit button while the "how" card is up.
  useEffect(() => {
    if (!how) return undefined;
    const btn = document.querySelector('#new-conversation-button-navbar button, #new-conversation-button-sidebar, [title="New Visit"]');
    if (!btn) return undefined;
    const prev = btn.style.cssText;
    btn.style.outline = '3px solid #2dce8f';
    btn.style.outlineOffset = '3px';
    btn.style.borderRadius = btn.style.borderRadius || '999px';
    btn.style.animation = 'cpt-ck-pulse 1.2s ease-in-out infinite';
    return () => { btn.style.cssText = prev; };
  }, [how]);

  // Running inside the extension IS "installed and signed in" — record it once.
  useEffect(() => {
    if (isCX && apiClient?.onboarding && !apiClient.onboarding.cx_authed && apiClient.markOnboarding) {
      apiClient.markOnboarding('cx_authed');
    }
  }, [isCX, apiClient, apiClient?.onboarding]);

  const cheerEl = cheer ? (
    <div className="cheer" aria-live="polite">
      <canvas id="cpt-ck-confetti" />
      <div className="msg">
        <div className="big">🏅</div>
        <h3>First real note.</h3>
        <p>Welcome to Comprehend — you’re live. That’s 4 of 5.</p>
      </div>
    </div>
  ) : null;
  if (!apiClient || !apiClient.email) return null;
  if (isPlaygroundOrigin()) return null; // the Playground draws its own
  // Account age gate (or `force_onboarding_checklist` for our own old test
  // accounts), demo accounts, "hide for good", all five done — one place.
  if (!onboardingChecklistActive(apiClient)) return null;
  const d = onboardingState(apiClient, isCX);
  const items = [
    ['account', 'Create your account', '', '1 min', false, null],
    ['basic', 'Playground Magic', 'Your first note, in a safe copy of your EMR', 'about 5 min', false, 'basic'],
    ['install', 'Install the extension', 'Add to Chrome, we sign you in', 'about 2 min', false, 'install'],
    ['real', 'Use it on a real visit', d.install ? 'Open your real EMR and hit Record' : 'Unlocks once the extension is installed', 'next visit', !d.install, 'real'],
    ['advanced', 'Advanced features', advancedHasNew(apiClient) ? 'Something new to try' : d.basic ? 'Chat, compliance, mics, and more' : 'Unlocks after Playground Magic', advancedHasNew(apiClient) ? 'new' : 'about 10 min', !d.basic, 'advanced'],
  ];
  const n = items.filter(([k]) => d[k]).length;
  if (n === 5) return null;
  const next = items.find(([k, , , , lock]) => !d[k] && !lock);
  const base = playgroundBase(apiClient);
  const go = (mod) => {
    // The real visit can't be practiced anywhere but the real EMR — explain,
    // point at New Visit, and let the first real note tick it off.
    if (mod === 'real') { setHow(true); return; }
    const u = `${base}/play?mode=onboard&module=${mod}`;
    window.open(u, '_blank', 'noopener');
  };
  const emrName = apiClient.EMR || 'your EMR';
  const ring = (
    <span className="ring" data-n={`${n}/5`} style={{ background: `conic-gradient(#2dce8f ${n * 20}%, rgba(255,255,255,.22) 0)` }} />
  );

  return (
    <div className="cpt-ck" aria-live="polite">
      <style>{CSS}</style>
      {cheerEl}
      {!open ? (
        <button type="button" className="pill" onClick={() => setOpen(true)}>
          {ring}
          <span>{next ? `Next: ${next[1]} · ${next[3]}` : 'Your checklist'}</span>
        </button>
      ) : (
        <div className="panel" role="dialog" aria-label="Onboarding checklist">
          <div className="hd">
            <button type="button" className="x" aria-label="Collapse" onClick={() => setOpen(false)}>×</button>
            <h3>Get to your first real note</h3>
            <small>{n} of 5 done</small>
            <div className="pbar"><i style={{ width: `${n * 20}%` }} /></div>
          </div>
          {how ? (
            <div className="how">
              <p><b>This one you do for real</b> — no Playground, no fake patient.</p>
              <ol>
                <li><span className="n">1</span><div>Open a real patient’s chart in <b>{emrName}</b> with this panel beside it.</div></li>
                <li><span className="n">2</span><div>Hit <b>＋ New Visit</b> up top — it picks up the patient from the chart.</div></li>
                <li><span className="n">3</span><div>Record the visit, then hit <b>Comprehend</b>. The note lands in {emrName}.</div></li>
              </ol>
              <p style={{ color: '#7d8683', fontSize: '12.5px' }}>We tick this off ourselves the moment your first real note lands.</p>
              <div className="btns">
                <button type="button" className="ok" onClick={() => setHow(false)}>Got it</button>
                <button type="button" className="back" onClick={() => setHow(false)}>Back to the list</button>
              </div>
            </div>
          ) : items.map(([k, nm, ds, tm, lock, mod]) => (
            <button
              type="button"
              key={k}
              className={`ci ${d[k] ? 'done' : lock ? 'lock' : next && next[0] === k ? 'next' : ''}`}
              disabled={d[k] || lock || !mod}
              onClick={() => mod && go(mod)}
            >
              <span className="box">{d[k] ? '✓' : lock ? '🔒' : ''}</span>
              <div>
                <div className="n">{nm}</div>
                {ds ? <div className="d">{ds}</div> : null}
                {!d[k] && !lock && mod ? <span className="go">{k === 'real' ? 'How do I do this? →' : 'Open the Playground →'}</span> : null}
              </div>
              <span className="t">{d[k] ? '' : tm}</span>
            </button>
          ))}
          <div className="ft">
            <span>Same list in the Playground and the extension</span>
            <button type="button" onClick={() => apiClient.markOnboarding && apiClient.markOnboarding('dismissed')}>Hide for good</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingChecklist;
